// Turns whatever a contenteditable produces into HTML that survives an email
// client.
//
// Browsers emit very different markup for the same visible result — <div>,
// <span style>, <font>, nested <b><b>, stray attributes from pasted content —
// and email clients (Outlook especially) render that inconsistently or not at
// all. So the editor's DOM is never stored directly: it's walked and rebuilt
// from a small allowlist, with the presentation re-applied as inline styles,
// which is the only styling email clients reliably honour.
//
// Also the sanitiser for pasted content. The composer is admin-only, but the
// output is mailed to thousands of people, and a stray <script> or
// javascript: href pasted from a web page must never survive into a campaign.

const BLOCK_STYLE = {
  p: 'margin:0 0 16px;',
  h2: 'margin:24px 0 12px;font-size:20px;font-weight:800;color:#292237;line-height:1.3;',
  h3: 'margin:20px 0 10px;font-size:17px;font-weight:700;color:#292237;line-height:1.3;',
  ul: 'margin:0 0 16px;padding-left:22px;',
  ol: 'margin:0 0 16px;padding-left:22px;',
  li: 'margin:0 0 6px;',
  blockquote:
    'margin:0 0 16px;padding:8px 0 8px 16px;border-left:3px solid #460069;color:#66626e;',
}

// Everything not named here is unwrapped — its text survives, its tag doesn't.
const TAG_MAP = {
  P: 'p', DIV: 'p',
  H1: 'h2', H2: 'h2',
  H3: 'h3', H4: 'h3', H5: 'h3', H6: 'h3',
  UL: 'ul', OL: 'ol', LI: 'li',
  BLOCKQUOTE: 'blockquote',
  STRONG: 'strong', B: 'strong',
  EM: 'em', I: 'em',
  A: 'a',
  BR: 'br',
}

const VOID_TAGS = new Set(['br'])

// Escaping follows the HTML serialisation algorithm exactly — the same one
// behind innerHTML — so the string this file emits is byte-for-byte what the
// browser reads back from the DOM after it is set. The editor relies on that
// equality to decide whether the DOM needs rewriting; when the two differed
// (a raw U+00A0 here versus &nbsp; from the browser) every blur replaced the
// editor's nodes, which threw away the saved selection and made "Add link"
// silently do nothing on any draft that contained a non-breaking space.
function escapeText(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/\u00a0/g, '&nbsp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function escapeAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/\u00a0/g, '&nbsp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Only links an email client should ever follow. A javascript: or data: href
// in a campaign is both a security problem and an instant spam signal.
export function safeHref(raw) {
  const href = String(raw ?? '').trim()
  if (!href) return null
  if (/^(https?:\/\/|mailto:)/i.test(href)) return href
  // A bare domain typed into the link prompt is a normal thing to do.
  if (/^[\w-]+(\.[\w-]+)+(\/|$)/.test(href)) return `https://${href}`
  return null
}

function serialize(node) {
  if (node.nodeType === 3) return escapeText(node.nodeValue)
  if (node.nodeType !== 1) return ''

  const tag = TAG_MAP[node.tagName]

  // Never descend into these — their text content is not content.
  if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED'].includes(node.tagName)) return ''

  if (tag === 'br') return '<br>'

  const inner = Array.from(node.childNodes).map(serialize).join('')

  // Unknown tag: keep the text, drop the wrapper.
  if (!tag) return inner

  if (tag === 'a') {
    const href = safeHref(node.getAttribute('href'))
    if (!href) return inner
    return `<a href="${escapeAttr(href)}" style="color:#460069;text-decoration:underline;">${inner}</a>`
  }

  // An empty block is a blank line the author probably didn't mean; an empty
  // inline tag is just noise.
  if (!inner.trim() && !VOID_TAGS.has(tag)) return ''

  const style = BLOCK_STYLE[tag]
  return style ? `<${tag} style="${style}">${inner}</${tag}>` : `<${tag}>${inner}</${tag}>`
}

// Bare text at the top level (common after a paste) has to become a paragraph,
// or it inherits no spacing at all in the email.
function wrapLooseInline(html) {
  return html.replace(
    /(^|<\/(?:p|h2|h3|ul|ol|blockquote)>)([^<]*(?:<(?:strong|em|a|br)\b[^>]*>.*?<\/(?:strong|em|a)>|<br>)?[^<]*)/g,
    (match, close, loose) =>
      loose.trim() ? `${close}<p style="${BLOCK_STYLE.p}">${loose.trim()}</p>` : match,
  )
}

export function sanitizeEmailHtml(html) {
  if (!html || !String(html).trim()) return ''
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  const out = Array.from(doc.body.childNodes).map(serialize).join('')
  return wrapLooseInline(out).trim()
}

// A plain-text rendering for the editor's empty check and for anywhere a
// preview needs words rather than markup.
export function htmlToText(html) {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  // Collapse non-breaking spaces, which contenteditable inserts freely.
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim()
}
